import { useState, useEffect, useMemo } from 'react';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { SelectionRow } from '../UISystem/Forms';
import { getProfileData } from '../../services/chat/profileService';
import { getAvailableSharingFields } from '../../utils/sharingFields';
import { commitSharedFields } from '../../services/sharing/commitSharedFields';
import { useConnectionData, useConnectionContext } from '../../contexts/ConnectionStateContext';
import { hasAboutMeContent as hasAboutMeContentOf } from '../../utils/profilePayload';

/**
 * InlineSharingList — the SHARED section's inline, directly-editable list of fields
 * you share with this person. Renders the SAME SelectionRow rows as the
 * SharingSelectionOverlay bottom sheet (so the two never diverge) and commits through
 * the SAME commitSharedFields path.
 *
 * Commit-on-leave: tapping a row flips the checkmark instantly (local state); the
 * actual share is committed once when the page navigates back, via commitRef — the
 * page calls commitRef.current() in its save-and-back handler. We only commit if the
 * selection changed.
 *
 * Shared-first (2026-08-18 frames): the list opens showing only what you're
 * actually sharing, labelled by value ("917-650-2256"), with a caret revealing
 * everything else. The split is taken from what was shared when the page opened,
 * not from live selection — otherwise a row you just unticked would vanish
 * mid-tap. It settles on the next open, which is when the commit has landed.
 *
 * @param {string} chatRoomId
 * @param {React.MutableRefObject} commitRef - The page sets commitRef.current() to fire on back
 */
const InlineSharingList = ({ chatRoomId, commitRef }) => {
  const connectionData = useConnectionData(chatRoomId);
  const { updateContactSharingStatus } = useConnectionContext();

  const [profileData, setProfileData] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  const [aboutMeEnabled, setAboutMeEnabled] = useState(false);
  const [hometownEnabled, setHometownEnabled] = useState(false);
  const [nameSharingFull, setNameSharingFull] = useState(true);
  const [dirty, setDirty] = useState(false);
  // Keys shared when the page opened — drives the split, so rows hold position.
  const [initiallyShared, setInitiallyShared] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Load profile and initialize the selection from what's currently shared.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const data = await getProfileData();
      if (cancelled) return;
      setProfileData(data);

      let sent = [];
      const sentStr = localStorage.getItem(`sent_shared_fields_${chatRoomId}`);
      if (sentStr) {
        try {
          sent = JSON.parse(sentStr);
        } catch {
          sent = [];
        }
      }

      const sel = {};
      getAvailableSharingFields(data).forEach((f) => {
        sel[f.key] = sent.includes(f.key);
      });
      setSelectedFields(sel);
      setInitiallyShared(new Set(sent));
      setAboutMeEnabled(sent.includes('aboutMe'));
      // Reflects current shares only — existing connections aren't retroactively
      // given hometown (default-on lives in the new-connection overlay, not here).
      setHometownEnabled(sent.includes('currentCity'));

      const ns =
        localStorage.getItem(`name_sharing_${chatRoomId}`) ||
        localStorage.getItem('profile_name_sharing');
      setNameSharingFull(ns ? ns === 'full' : true);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [chatRoomId]);

  const availableFields = useMemo(() => getAvailableSharingFields(profileData), [profileData]);

  const hasAboutMeContent = hasAboutMeContentOf(profileData);

  // Commit on leave — the page calls commitRef.current() from save-and-back.
  useEffect(() => {
    if (!commitRef) return undefined;
    commitRef.current = async () => {
      if (!dirty || !profileData) return;
      const sharedFields = ['photoUrl']; // Always share photo
      availableFields.forEach((f) => {
        if (selectedFields[f.key]) sharedFields.push(f.key);
      });
      if (aboutMeEnabled && hasAboutMeContent) sharedFields.push('aboutMe');
      if (hometownEnabled && profileData.currentCity) sharedFields.push('currentCity');

      await commitSharedFields({
        chatRoomId,
        sharedFields,
        isFullName: nameSharingFull,
        profileData,
        connectionData,
        updateContactSharingStatus,
        shareMethod: 'detail_page',
      });
    };
    return () => {
      commitRef.current = null;
    };
  }, [
    commitRef,
    dirty,
    profileData,
    availableFields,
    selectedFields,
    aboutMeEnabled,
    hometownEnabled,
    hasAboutMeContent,
    nameSharingFull,
    connectionData,
    chatRoomId,
    updateContactSharingStatus,
  ]);

  const toggleField = (key) => {
    setDirty(true);
    setSelectedFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!profileData) return null;

  const wasShared = (key) => !!initiallyShared?.has(key);
  const sharedContactFields = availableFields.filter((f) => wasShared(f.key));
  const restContactFields = availableFields.filter((f) => !wasShared(f.key));
  const hometownHidden = !!profileData.currentCity && !wasShared('currentCity');
  const aboutMeHidden = hasAboutMeContent && !wasShared('aboutMe');
  const hiddenCount = restContactFields.length + (hometownHidden ? 1 : 0) + (aboutMeHidden ? 1 : 0);

  const hometownRow = (
    <SelectionRow
      icon={MapPin}
      label="Hometown"
      isSelected={hometownEnabled}
      onClick={() => {
        setDirty(true);
        setHometownEnabled((v) => !v);
      }}
      showCheckmark={true}
      surface="detail"
    />
  );

  const aboutMeRow = (
    <SelectionRow
      icon={User}
      label="About me"
      isSelected={aboutMeEnabled}
      onClick={() => {
        setDirty(true);
        setAboutMeEnabled((v) => !v);
      }}
      showCheckmark={true}
      surface="detail"
    />
  );

  const contactRow = ({ key, icon, valueLabel }) => (
    <SelectionRow
      key={key}
      icon={icon}
      // Labelled by value on this surface — the bottom sheet keeps type labels
      label={valueLabel}
      isSelected={!!selectedFields[key]}
      onClick={() => toggleField(key)}
      showCheckmark={true}
      surface="detail"
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Full name toggle — a single name has nothing to toggle */}
      {profileData.name?.includes(' ') ? (
        <SelectionRow
          icon={null}
          label={nameSharingFull ? profileData.name : profileData.name.split(' ')[0]}
          isSelected={nameSharingFull}
          onClick={() => {
            setDirty(true);
            setNameSharingFull((v) => !v);
          }}
          showCheckmark={true}
          surface="detail"
        />
      ) : (
        <SelectionRow
          icon={null}
          label={profileData.name || 'Name'}
          isSelected={true}
          showCheckmark={true}
          surface="detail"
        />
      )}

      {/* What you're actually sharing */}
      {!hometownHidden && profileData.currentCity && hometownRow}
      {!aboutMeHidden && hasAboutMeContent && aboutMeRow}
      {sharedContactFields.map(contactRow)}

      {/* Everything else, behind the caret */}
      {expanded && (
        <>
          {hometownHidden && hometownRow}
          {aboutMeHidden && aboutMeRow}
          {restContactFields.map(contactRow)}
        </>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          aria-label={expanded ? 'Hide unshared fields' : 'Show what else you could share'}
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            marginTop: '2px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <CaretDown
            size={18}
            color="#C7C7CC"
            weight="regular"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          />
        </button>
      )}
    </div>
  );
};

export default InlineSharingList;
